import { MakeGenerics, Navigate, Route } from 'react-location';
// react query
import {
  CurrentUser,
  fetchCurrentUser,
  queryKey as currentUserQueryKey,
} from 'src/hooks/core-api/useCurrentUser';
import { authenticationStatusSubject, queryClient } from 'src/index';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// pages
import AwaitingInstallation from 'src/pages/AwaitingInstallationPage';
import CallHistory from 'src/pages/call-history/CallHistoryPage';
import DashboardHome from 'src/pages/dashboard/DashboardHomePage';
import LocationSelectionRequired from 'src/pages/LocationSelectionRequired';
import Onboarding from 'src/pages/onboarding/OnboardingPage';
import NotFound from 'src/pages/Page404';
import PageError from 'src/pages/Page500';
import VoicemailSettings from 'src/pages/voicemail-settings/VoicemailSettingsPage';
import VoicemailList from 'src/pages/voicemail/VoicemailListPage';
import VoicemailRoot from 'src/pages/voicemail/VoicemailRootPage';
import VoicemailSingle from 'src/pages/voicemail/VoicemailSinglePage';
// components
import LoadingScreen from 'src/components/LoadingScreen';
// store
import useMainStore from 'src/store';

export type LocationGenerics = MakeGenerics<{
  LoaderData: {
    user: CurrentUser;
  };
  Params: {};
  Search: {
    payment_failed?: boolean;
  };
}>;

const loadUser = () =>
  new Promise<LocationGenerics['LoaderData']>((resolve, reject) => {
    console.log('Loading user data in router....');
    // Waits for the user to be authenticated before fetching data
    authenticationStatusSubject.subscribe(async (isAuthenticated) => {
      console.log('Authentication status changed in `loadUser` in router: ', isAuthenticated);
      if (isAuthenticated) {
        try {
          const currentUser: CurrentUser =
            queryClient.getQueryData(currentUserQueryKey) ??
            (await queryClient.fetchQuery(currentUserQueryKey, fetchCurrentUser));
          // Set initial location
          const { locations } = currentUser;

          if (locations.length !== 0) {
            // Needed by global navbar location select
            useMainStore.setState({ selectedLocations: [currentUser.locations[0]] });
          }

          console.log('Finishing load user data in router....');
          resolve({
            user: currentUser,
          });
        } catch (error) {
          console.error(error);
          reject(error);
        }
      } else {
        console.error("User isn't authenticated");
        reject(new Error('Not authenticated before loading initial routes.'));
      }
    });
  });

const routes: Route<LocationGenerics>[] = [
  {
    path: '/',
    element: <Navigate to="/dashboard/home" />,
  },
  {
    path: 'waiting-for-installation',
    element: (
      <LogoOnlyLayout>
        <AwaitingInstallation />
      </LogoOnlyLayout>
    ),
  },
  {
    path: 'onboarding',
    element: <Onboarding />,
    loader: loadUser,
    errorElement: (
      <LogoOnlyLayout>
        <PageError />
      </LogoOnlyLayout>
    ),
    pendingElement: <LoadingScreen />,
    pendingMs: 0,
    pendingMinMs: 2500, // If it's shown, ensure the pending element is rendered for at least 2500ms
  },

  {
    path: 'dashboard',
    loader: loadUser,
    element: <DashboardLayout />,
    errorElement: (
      <LogoOnlyLayout>
        <PageError />
      </LogoOnlyLayout>
    ),
    pendingElement: <LoadingScreen />,
    pendingMs: 0,
    pendingMinMs: 2500, // If it's shown, ensure the pending element is rendered for at least 2500ms
    children: [
      {
        path: '/',
        element: <Navigate to="/dashboard/home" />,
      },
      {
        path: 'home',
        element: (
          <LocationSelectionRequired>
            <DashboardHome />
          </LocationSelectionRequired>
        ),
      },
      {
        path: 'call-history',
        element: (
          <LocationSelectionRequired>
            <CallHistory />
          </LocationSelectionRequired>
        ),
      },
      {
        path: 'voicemail',
        element: (
          <LocationSelectionRequired>
            <VoicemailRoot />
          </LocationSelectionRequired>
        ),
        children: [
          {
            path: '/',
            element: <VoicemailList />,
          },
          {
            path: ':voicemailId',
            element: <VoicemailSingle />,
          },
        ],
      },
      {
        path: 'voicemail-settings',
        element: (
          <LocationSelectionRequired singleLocation={true}>
            <VoicemailSettings />
          </LocationSelectionRequired>
        ),
      },
      {
        path: '*',
        element: <Navigate to="/404" replace={true} />,
      },
    ],
  },

  {
    path: '*',
    element: <LogoOnlyLayout />,
    children: [
      {
        path: '404',
        element: <NotFound />,
      },
      {
        path: '500',
        element: <PageError />,
      },
      {
        path: '*',
        element: <Navigate to="404" replace={true} />,
      },
    ],
  },
];

export { routes };
